import { useState, type ChangeEvent } from 'react';
import { toast } from 'sonner';
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle, PlayCircle } from 'lucide-react';
import { useImportRoles, type RolesImportReport } from '../../hooks/useRoles';
import { CollapsibleSection } from '../../components/ui/CollapsibleSection';
import { Button } from '../../components/ui/Button';
import { ApiError } from '../../lib/api';

// Matches the importer's header aliases ("Role", "Position", "CAFM Key",
// "Ref" and "Group" are tolerated too). Category and External Ref are
// optional; category names must already exist under Role categories.
const TEMPLATE_CSV = `Name,Category,External Ref
M&E Engineer,Engineer,CAFM-ME-001
Plumber,Engineer,CAFM-PL-002
Receptionist,Office,
`;

/**
 * Bulk CSV import panel for the Job roles admin page, collapsed by
 * default so the everyday create/edit flow keeps the space. Same flow
 * as the attendee importer: read the file client-side, POST the text,
 * dry-run on by default so the first click never persists.
 */
export function RolesImportCard() {
  const importMutation = useImportRoles();
  const [csv, setCsv] = useState<string | null>(null);
  const [filename, setFilename] = useState<string | null>(null);
  const [dryRun, setDryRun] = useState(true);
  const [report, setReport] = useState<RolesImportReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  function downloadTemplate() {
    const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'roles-import-template.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  }

  function onFile(e: ChangeEvent<HTMLInputElement>) {
    setError(null);
    setReport(null);
    const file = e.target.files?.[0];
    if (!file) return;
    setFilename(file.name);
    const reader = new FileReader();
    reader.onload = () => setCsv(String(reader.result ?? ''));
    reader.onerror = () => setError('failed to read file');
    reader.readAsText(file);
  }

  async function onSubmit() {
    if (!csv) return;
    setError(null);
    try {
      const r = await importMutation.mutateAsync({ csv, dry_run: dryRun });
      setReport(r);
      if (!r.dry_run) {
        toast.success(`Imported ${r.created} new, ${r.updated} updated, ${r.skipped} unchanged`);
      }
    } catch (err) {
      setReport(null);
      const msg = err instanceof ApiError ? err.message : 'import failed';
      setError(msg);
      toast.error(msg);
    }
  }

  return (
    <CollapsibleSection
      icon={<Upload className="h-4 w-4 text-fg-muted" />}
      title="Import from CSV"
    >
      <div className="space-y-4">
        <p className="text-sm text-fg-muted">
          One role per row. Required column:
          <code className="mx-1 rounded bg-surface-2 px-1 text-xs">Name</code>.
          Optional:
          <code className="mx-1 rounded bg-surface-2 px-1 text-xs">Category</code>
          (must already exist under Role categories) and
          <code className="mx-1 rounded bg-surface-2 px-1 text-xs">External Ref</code>
          (CAFM key). Existing roles are matched by name; a new category or ref
          updates them, blank cells leave them untouched.{' '}
          <button
            type="button"
            onClick={downloadTemplate}
            className="font-medium text-fg-muted underline underline-offset-2 hover:text-fg"
          >
            Download template CSV
          </button>
        </p>

        <label
          htmlFor="roles-csv-file"
          className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-token-sm border-2 border-dashed px-6 py-6 text-center transition-colors ${
            csv
              ? 'border-success/25 bg-success-soft/15'
              : 'border-line-strong/20 bg-surface-2 hover:bg-surface-inset'
          }`}
        >
          {csv ? (
            <>
              <FileSpreadsheet className="h-6 w-6 text-success" />
              <div className="text-sm font-semibold text-success-soft-fg">{filename}</div>
              <div className="text-xs text-success-soft-fg">Click to choose a different file</div>
            </>
          ) : (
            <>
              <Upload className="h-6 w-6 text-fg-dim" />
              <div className="text-sm font-semibold text-fg-muted">Click to choose a CSV file</div>
            </>
          )}
          <input
            id="roles-csv-file"
            type="file"
            accept=".csv,text/csv"
            onChange={onFile}
            className="sr-only"
          />
        </label>

        <label className="flex cursor-pointer items-start gap-3 rounded-token-sm border border-line/10 p-3 transition-colors hover:bg-surface-2">
          <input
            type="checkbox"
            checked={dryRun}
            onChange={(e) => setDryRun(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-line-strong/20"
          />
          <div className="text-sm">
            <div className="font-medium">Dry-run (validate only)</div>
            <div className="text-xs text-fg-muted">
              Show what would happen without writing anything. Untick to actually import.
            </div>
          </div>
        </label>

        {error ? (
          <div className="flex items-start gap-2 rounded-token-sm bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        ) : null}

        <div className="flex justify-end">
          <Button
            variant="primary"
            size="sm"
            iconLeft={<PlayCircle className="h-4 w-4" />}
            disabled={!csv}
            loading={importMutation.isPending}
            onClick={onSubmit}
          >
            {dryRun ? 'Run dry-run' : 'Apply import'}
          </Button>
        </div>

        {report ? (
          <div className="rounded-token-sm border border-line/10 bg-surface-2 p-3">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <CheckCircle2
                className={`h-4 w-4 ${report.errors.length === 0 ? 'text-success' : 'text-warning'}`}
              />
              {report.dry_run ? 'Dry-run report' : 'Import report'}
            </div>
            <p className="mt-1 text-sm text-fg-muted">
              {report.total} row{report.total === 1 ? '' : 's'}:{' '}
              {report.dry_run ? 'would create' : 'created'} {report.created},{' '}
              {report.dry_run ? 'would update' : 'updated'} {report.updated},{' '}
              unchanged {report.skipped}.
            </p>
            {report.errors.length > 0 ? (
              <ul className="mt-2 space-y-1 text-xs text-danger-soft-fg">
                {report.errors.map((e) => (
                  <li key={e.row}>
                    <span className="font-mono">row {e.row}</span>: {e.message}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </div>
    </CollapsibleSection>
  );
}
